import { Ionicons } from "@expo/vector-icons";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
// eslint-disable-next-line import/no-named-default
import type { default as MomentLib } from "moment";
import { IconButton, useTheme } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";

import { Scale } from "../constants";
import { HORMONE_HEX_COLOR } from "../constants/theme";
import { getCurrentMenstrualCyclePhaseFromUser, MenstrualCyclePhase } from "../helpers/userHelpers";
import { useExternalLink } from "../hooks/useExternalLink";
import type { User } from "../services/backendTypes";

interface MenstrualCyclePhaseIndicatorProps {
  user: User | null;
  date: MomentLib.Moment;
}

const MenstrualCyclePhaseIndicator = ({ user, date }: MenstrualCyclePhaseIndicatorProps): JSX.Element | null => {
  const { openLink } = useExternalLink();
  const { t } = useTranslation();
  const theme = useTheme();

  if (!user) return null;

  // Convert moment date to JavaScript Date object
  const jsDate = date.toDate();

  // Pass the date to getCurrentMenstrualCyclePhaseFromUser
  const phase = getCurrentMenstrualCyclePhaseFromUser(user, jsDate);
  if (!phase) return null;

  const getPhaseInfoUrl = (cyclePhase: MenstrualCyclePhase): string =>
    "https://killerbodyfood.com/blogs/voedingstips/tagged/menstruatiecyclus";

  const phaseColor = HORMONE_HEX_COLOR;
  const phaseInfoUrl = getPhaseInfoUrl(phase);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: Scale(8),
        marginHorizontal: Scale(16),
        backgroundColor: "transparent",
      }}
    >
      {/* Empty view for left side spacing */}
      <View style={{ width: Scale(32) }} />

      {/* Phase indicator with background - centered */}
      <View
        style={{
          backgroundColor: `${phaseColor}20`,
          paddingVertical: Scale(6),
          paddingHorizontal: Scale(12),
          borderRadius: theme.borderRadii.badge.pill,
          flexDirection: "row",
          alignItems: "center",
          alignSelf: "center",
        }}
      >
        <Ionicons name="female-outline" size={16} color={phaseColor} style={{ marginRight: Scale(4) }} />
        <Text
          style={{
            fontWeight: "600",
            color: phaseColor,
            textAlign: "center",
          }}
        >
          {t(`general.menstrual_cycle_phases.${phase}`)}
        </Text>
      </View>

      {/* Information button with outline style - right aligned */}
      <IconButton
        size="sm"
        variant="outline"
        borderRadius="full"
        width={Scale(32)}
        height={Scale(32)}
        borderColor={phaseColor}
        borderWidth={1.5}
        _icon={{
          as: Ionicons,
          name: "information",
          color: phaseColor,
          size: "md",
        }}
        _pressed={{
          backgroundColor: `${phaseColor}20`,
        }}
        onPress={() => {
          openLink(phaseInfoUrl).catch((err) => {
            console.error("Failed to open cycle phase info URL:", err);
            Sentry.captureException(err);
          });
        }}
        testID="cycle-phase-info-button"
      />
    </View>
  );
};

export default MenstrualCyclePhaseIndicator;
